import _ from 'lodash'
import { mapGetters, mapActions } from 'vuex'
import {
	DEFAULT_OFFICE_TIMINGS,
	OFFICE_DAYS,
	EMPTY_SCHEDULE_ERROR_MESSAGE,
	IVR_OFFICE_HOURS_ERROR_MESSAGE,
} from '@/config/constants'

const isScheduleTimeEmpty = v => v == null || v === ''

/**
 * Shared office hours schedule logic for SchedulePanel and settings sections.
 */
export default {
	computed: {
		/**
		 * Whether the active partner has an IVR configured.
		 *
		 * @return {Boolean}
		 */
		isIVRExist() {
			return this.partner?.ivr_exists
		},

		/**
		 * Whether the active partner has lunch IVR configured.
		 *
		 * @return {Boolean}
		 */
		isLunchIVRExist() {
			return this.partner?.lunch_ivr_exists
		},

		/**
		 * True when every day is closed and all time fields are empty.
		 *
		 * @return {Boolean}
		 */
		scheduleIsEmpty() {
			const isEveryClosed = this.officeHours.every(day => !day.is_open)
			const isEveryTimeEmpty = this.officeHours.every(
				day =>
					isScheduleTimeEmpty(day.starts_at) &&
					isScheduleTimeEmpty(day.ends_at) &&
					isScheduleTimeEmpty(day.lunch_starts_at) &&
					isScheduleTimeEmpty(day.lunch_ends_at)
			)
			return isEveryClosed && isEveryTimeEmpty
		},

		/**
		 * True when every lunch time field is empty.
		 *
		 * @return {Boolean}
		 */
		isEveryLunchTimeEmpty() {
			return this.officeHours.every(
				day =>
					isScheduleTimeEmpty(day.lunch_starts_at) &&
					isScheduleTimeEmpty(day.lunch_ends_at)
			)
		},

		/**
		 * True when lunch IVR exists but lunch times are empty.
		 *
		 * @return {Boolean}
		 */
		emptyLunchWhileLunchIvr() {
			return this.isLunchIVRExist && this.isEveryLunchTimeEmpty
		},

		/**
		 * Disable save when IVR rules are not satisfied.
		 *
		 * @return {Boolean}
		 */
		disableSavebtn() {
			const ivrNeedsSchedule = this.isIVRExist && this.scheduleIsEmpty
			return ivrNeedsSchedule || this.emptyLunchWhileLunchIvr
		},

		/**
		 * Validation message shown when save is disabled.
		 *
		 * @return {String}
		 */
		errorMessage() {
			return this.emptyLunchWhileLunchIvr
				? IVR_OFFICE_HOURS_ERROR_MESSAGE
				: EMPTY_SCHEDULE_ERROR_MESSAGE
		},

		...mapGetters({ partner: 'partners/active' }),
	},

	methods: {
		/**
		 * Load office hours from the API.
		 *
		 * @return {Promise<void>}
		 */
		async fetchOfficeHours() {
			try {
				this.isFetchingOfficeHours = true
				const response = await this.$api
					.partners(this.partner.id)
					.schedules()
					.get()

				const apiData = response.get('data', [])
				this.updateOfficeHoursFromAPI(apiData)
			} catch (e) {
				this.$alert.error(e)
			} finally {
				this.isFetchingOfficeHours = false
			}
		},

		/**
		 * Update open/closed state for a day and apply defaults when opening.
		 *
		 * @param {Number} index
		 * @param {Boolean} isChecked
		 * @return {void}
		 */
		updateOpenStatus(index, isChecked) {
			this.$set(this.officeHours[index], 'is_open', isChecked)

			if (isChecked) {
				const defaults = DEFAULT_OFFICE_TIMINGS
				const timing = this.officeHours[index]
				const keys = Object.keys(defaults)
				const hasAllEmpty = keys.every(key => !timing[key])

				if (hasAllEmpty) {
					for (const [key, defaultValue] of Object.entries(
						defaults
					)) {
						if (!this.officeHours[index][key]) {
							this.$set(
								this.officeHours[index],
								key,
								defaultValue
							)
						}
					}
				}
			}
		},

		/**
		 * Update a single schedule field for the given day.
		 *
		 * @param {String} dayName
		 * @param {String} field
		 * @param {String} value
		 * @return {void}
		 */
		updateField(dayName, field, value) {
			const dayIndex = this.officeHours.findIndex(
				d => d.day_name === dayName
			)
			if (dayIndex !== -1) {
				this.$set(this.officeHours[dayIndex], field, value)
			}
		},

		/**
		 * Normalize end-of-day midnight input to 23:59.
		 *
		 * @param {String} dayName
		 * @param {String} field
		 * @param {String} value
		 * @return {void}
		 */
		onTimeBlur(dayName, field, value) {
			if (value === '00:00') {
				this.updateField(dayName, field, '23:59')
			}
		},

		/**
		 * Merge API schedule data into local office hours state.
		 *
		 * @param {Array} apiData
		 * @return {void}
		 */
		updateOfficeHoursFromAPI(apiData) {
			this.officeHours = this.officeHours.map(localDay => {
				const apiDay = apiData.find(
					d =>
						d.day_name.toLowerCase() ===
						localDay.day_name.toLowerCase()
				)
				if (!apiDay) {
					return localDay
				}

				return {
					...this.normalizeOfficeDay({
						...localDay,
						...apiDay,
						day_name: localDay.day_name.toLowerCase(),
					}),
					day_name: localDay.day_name,
				}
			})

			this.extracted = this.extractedProperties(this.officeHours)
		},

		/**
		 * Pick comparable fields from schedule rows for change detection.
		 *
		 * @param {Array} arr
		 * @return {Array}
		 */
		extractedProperties(arr) {
			return arr.map(
				({
					starts_at,
					ends_at,
					lunch_starts_at,
					lunch_ends_at,
					day_name,
					is_open,
				}) => ({
					starts_at: starts_at ?? null,
					ends_at: ends_at ?? null,
					lunch_starts_at: lunch_starts_at ?? null,
					lunch_ends_at: lunch_ends_at ?? null,
					day_name: day_name ?? null,
					is_open: is_open ?? false,
				})
			)
		},

		/**
		 * Compare two schedule arrays for equality.
		 *
		 * @param {Array} arr1
		 * @param {Array} arr2
		 * @return {Boolean}
		 */
		isOfficeHoursNotChanged(arr1, arr2) {
			const x = this.extractedProperties(arr1)
			const i = this.extractedProperties(arr2)
			const y = x.map(day => ({
				...day,
				day_name: day.day_name.toLowerCase(),
			}))
			const z = i.map(day => ({
				...day,
				day_name: day.day_name.toLowerCase(),
			}))
			const a = _.sortBy(y, ['day_name'])
			const b = _.sortBy(z, ['day_name'])
			return _.isEqual(a, b)
		},

		/**
		 * Normalize a single day for API payload.
		 *
		 * @param {Object} day
		 * @return {Object}
		 */
		normalizeOfficeDay(day) {
			return {
				...day,
				day_name: day.day_name.toLowerCase(),
				is_open: day.is_open ?? false,
				starts_at: day.starts_at ? day.starts_at : null,
				lunch_starts_at: day.lunch_starts_at
					? day.lunch_starts_at
					: null,
				lunch_ends_at: day.lunch_ends_at ? day.lunch_ends_at : null,
				ends_at: day.ends_at ? day.ends_at : null,
			}
		},

		/**
		 * Build the API payload from current office hours.
		 *
		 * @return {Array}
		 */
		getModifiedPayload() {
			return this.officeHours.map(day => this.normalizeOfficeDay(day))
		},

		/**
		 * Lowercase day names for API submission.
		 *
		 * @return {Array}
		 */
		daynameToLowerCase() {
			return this.officeHours?.map(d => ({
				...d,
				day_name: d.day_name.toLowerCase(),
			}))
		},

		/**
		 * Whether time inputs should render for a day row.
		 *
		 * @param {Object} day
		 * @param {String} field
		 * @return {Boolean}
		 */
		shouldShowTimeField(day, field) {
			if (day.is_open) {
				return true
			}

			return !this.isTimeValueEmpty(day[field])
		},

		/**
		 * Whether a schedule time value is empty (null or blank).
		 *
		 * @param {String|null} value
		 * @return {Boolean}
		 */
		isTimeValueEmpty(value) {
			return isScheduleTimeEmpty(value)
		},

		/**
		 * Cell display mode for a time field: dash, all-day label, or time input.
		 *
		 * @param {Object} day
		 * @param {String} field
		 * @return {'dash'|'all-day'|'input'}
		 */
		getTimeCellMode(day, field) {
			if (!this.shouldShowTimeField(day, field)) {
				return 'dash'
			}

			if (
				day.is_open &&
				this.isTimeValueEmpty(day[field]) &&
				this.isEditing === false
			) {
				return 'placeholder'
			}

			return 'input'
		},

		/**
		 * Persist office hours to the API.
		 *
		 * @return {Promise<void>}
		 */
		async officeHoursSubmit() {
			try {
				this.busy = true
				const payload = this.daynameToLowerCase()

				for (const day of payload) {
					if (day?.is_open) {
						if (!day.starts_at && !day.ends_at) {
							this.$alert.error(
								'Office start and end times are required.'
							)
							return
						}

						if (
							Boolean(day.starts_at) !== Boolean(day.ends_at) ||
							day.starts_at > day.ends_at
						) {
							this.$alert.error('Please enter a valid time.')
							return
						}

						if (
							Boolean(day.lunch_starts_at) !==
								Boolean(day.lunch_ends_at) ||
							day.lunch_starts_at > day.lunch_ends_at
						) {
							this.$alert.error('Please enter a valid time.')
							return
						}

						if (day.lunch_starts_at && day.lunch_ends_at) {
							if (
								day.lunch_starts_at < day.starts_at ||
								day.lunch_ends_at > day.ends_at
							) {
								this.$alert.error(
									'Lunch time must be within office hours.'
								)
								return
							}
						}

						if (day.starts_at && day.ends_at) {
							if (day.starts_at === day.ends_at) {
								this.$alert.error(
									'Office start and end times cannot be the same.'
								)
								return
							}
						}

						if (day.lunch_starts_at && day.lunch_ends_at) {
							if (day.lunch_starts_at === day.lunch_ends_at) {
								this.$alert.error(
									'Lunch start and end times cannot be the same.'
								)
								return
							}
						}
					}
				}

				const perfectPayload = this.getModifiedPayload()

				const response = await this.$api
					.partners(this.partner.id)
					.schedules()
					.create(perfectPayload)

				const data = response.get('data', [])

				await this.getOfficestatus(data)

				this.updateOfficeHoursFromAPI(data)

				this.$alert.success(
					response?.response?.message ||
						'Office hours saved successfully.'
				)

				return true
			} catch (e) {
				this.$alert.error(
					e?.response?.errors?.schedules?.[0] ||
						e?.response?.message ||
						e?.message
				)
				return false
			} finally {
				this.busy = false
			}
		},

		/**
		 * Reset office hours to the default week template.
		 *
		 * @return {void}
		 */
		clearOfficeHours() {
			this.officeHours = OFFICE_DAYS.map(i => ({
				day_name: i,
				starts_at: null,
				ends_at: null,
				is_open: false,
				lunch_starts_at: null,
				lunch_ends_at: null,
			}))
		},

		...mapActions({
			getOfficestatus: 'settings/getOfficestatus',
			fetchPartner: 'partners/find',
		}),
	},

	data() {
		return {
			officeHours: OFFICE_DAYS.map(i => ({
				day_name: i,
				starts_at: null,
				ends_at: null,
				is_open: false,
				lunch_starts_at: null,
				lunch_ends_at: null,
			})),
			busy: false,
			extracted: [],
			isFetchingOfficeHours: false,
		}
	},
}
